import {
  Activity,
  Building2,
  ChevronDown,
  CircleDollarSign,
  Coins,
  DoorOpen,
  FileArchive,
  FileText,
  KeyRound,
  LayoutDashboard,
  Mail,
  Menu,
  MonitorCog,
  Package,
  ReceiptText,
  Route,
  Settings2,
  ShieldAlert,
  ShieldCheck,
  Users,
  Workflow,
} from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { ADMIN_PERMISSIONS, type AdminPermission } from '@/auth/permissions';
import { cn } from '@/lib/utils';
import { adminNavigationPath, TUTTI_V2_NAVIGATION } from '@/navigation/tuttiV2';
import { authStore, brandStore } from '@/stores';
import { isNextopBrand } from '@/utils/brand';
import { useSidebar } from './SidebarContext';

interface MenuItem {
  title: string;
  path: string;
  icon: React.ComponentType<{ className?: string }>;
  exact?: boolean;
  openInNewTab?: boolean;
  requiredPermission?: AdminPermission;
}

interface MenuGroup {
  key: string;
  title: string;
  items: MenuItem[];
}

export const Sidebar = observer(() => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {
      global: true,
      brand: true,
      [TUTTI_V2_NAVIGATION.key]: true,
    },
  );
  const { isCollapsed, toggleCollapsed } = useSidebar();
  const activeBrand = brandStore.activeBrand;
  const showNextopTools = isNextopBrand(activeBrand);

  const brandBasePath = activeBrand ? `/brands/${activeBrand.id}` : '';
  const menuGroups: MenuGroup[] = [
    {
      key: 'global',
      title: '全局',
      items: [
        {
          title: 'Tutti 权限管理',
          path: '/tutti-permissions',
          icon: ShieldCheck,
        },
        {
          title: 'Brand 管理',
          path: '/brands',
          icon: Building2,
          exact: true,
        },
        {
          title: '选择 Brand',
          path: '/brand-selection',
          icon: LayoutDashboard,
          exact: true,
        },
        {
          title: '管理密钥',
          path: '/admin-tokens',
          icon: KeyRound,
          exact: true,
        },
        {
          title: '告警归因',
          path: '/alert-attributions/http-failures',
          icon: ShieldAlert,
          requiredPermission: ADMIN_PERMISSIONS.alertAttributionView,
        },
        {
          title: '接口异常查询',
          path: '/http-exceptions',
          icon: Activity,
          requiredPermission: ADMIN_PERMISSIONS.alertAttributionView,
        },
        {
          title: 'Session 日志',
          path: '/session-logs',
          icon: FileArchive,
          requiredPermission: ADMIN_PERMISSIONS.sessionLogsView,
        },
        {
          title: '任务',
          path: '/tasks',
          icon: Workflow,
          requiredPermission: ADMIN_PERMISSIONS.taskPlatformView,
        },
      ],
    },
    {
      key: 'brand',
      title: activeBrand ? `${activeBrand.name} 运营` : 'Brand 运营',
      items: activeBrand
        ? [
            {
              title: '测试管理',
              path: `${brandBasePath}/test-management`,
              openInNewTab: true,
              icon: Workflow,
            },
            {
              title: '登录码管理',
              path: `${brandBasePath}/login-codes`,
              icon: KeyRound,
            },
            {
              title: '邮箱邀请码名单',
              path: `${brandBasePath}/landing-waitlist`,
              icon: Mail,
            },
            {
              title: '内测管理',
              path: `${brandBasePath}/beta-management`,
              icon: KeyRound,
            },
            {
              title: '白名单管理',
              path: `${brandBasePath}/whitelist`,
              icon: Users,
            },
            {
              title: '用户配置管理',
              path: `${brandBasePath}/user-configs`,
              icon: Settings2,
            },
            {
              title: '订单管理',
              path: `${brandBasePath}/commerce/orders`,
              icon: ReceiptText,
              requiredPermission: ADMIN_PERMISSIONS.commerceOrdersRead,
            },
            {
              title: '积分管理',
              path: `${brandBasePath}/commerce/credits`,
              icon: CircleDollarSign,
              requiredPermission: ADMIN_PERMISSIONS.commerceCreditsView,
            },
            {
              title: '成本看板',
              path: `${brandBasePath}/commerce/cost-dashboard`,
              icon: LayoutDashboard,
              requiredPermission: ADMIN_PERMISSIONS.commerceCostsView,
            },
            ...(showNextopTools
              ? [
                  {
                    title: '公共房间',
                    path: `${brandBasePath}/public-rooms`,
                    icon: DoorOpen,
                  },
                  {
                    title: 'Room 日志',
                    path: `${brandBasePath}/room-debug-logs`,
                    icon: FileArchive,
                  },
                ]
              : []),
            ...(showNextopTools
              ? [
                  {
                    title: '连接器管理',
                    path: `${brandBasePath}/connectors`,
                    icon: Package,
                  },
                  {
                    title: 'Skill 市场',
                    path: `${brandBasePath}/skills`,
                    icon: Package,
                  },
                  {
                    title: '应用管理',
                    path: `${brandBasePath}/applications`,
                    icon: Package,
                  },
                  {
                    title: '协作版版本更新',
                    path: `${brandBasePath}/desktop-updates`,
                    icon: FileArchive,
                    requiredPermission: ADMIN_PERMISSIONS.desktopUpdatesView,
                  },
                ]
              : []),
          ]
        : [
            {
              title: '先选择 Brand',
              path: '/brand-selection',
              icon: KeyRound,
            },
          ],
    },
    {
      key: TUTTI_V2_NAVIGATION.key,
      title: TUTTI_V2_NAVIGATION.title,
      items: [
        {
          title: '渠道归因',
          path: '/acquisition-attributions',
          icon: Route,
        },
        {
          title: '房间配额管理',
          path: '/room-capacity',
          icon: Users,
          requiredPermission: ADMIN_PERMISSIONS.roomCapacityView,
        },
        {
          title: '房间巡检',
          path: '/room-inspector',
          icon: DoorOpen,
          requiredPermission: ADMIN_PERMISSIONS.roomInspectorView,
        },
        {
          title: 'Tutti Computer',
          path: adminNavigationPath(TUTTI_V2_NAVIGATION.routes.computers),
          icon: MonitorCog,
        },
        {
          title: '更新消息',
          path: adminNavigationPath(TUTTI_V2_NAVIGATION.routes.productUpdates),
          icon: Mail,
        },
        {
          title: 'Computer 发布信息',
          path: adminNavigationPath(
            TUTTI_V2_NAVIGATION.routes.computerReleaseNotes,
          ),
          icon: FileText,
        },
        {
          title: 'Agent 包审核',
          path: adminNavigationPath(TUTTI_V2_NAVIGATION.routes.agentPackages),
          icon: Package,
        },
        {
          title: 'Computer Production 强制升级',
          path: adminNavigationPath(
            TUTTI_V2_NAVIGATION.routes.computerProductionUpgrade,
          ),
          icon: FileArchive,
          requiredPermission: ADMIN_PERMISSIONS.desktopUpdatesView,
        },
      ],
    },
  ];

  const toggleGroup = (groupTitle: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupTitle]: !prev[groupTitle],
    }));
  };

  menuGroups.splice(1, 0, {
    key: 'app-config',
    title: 'AppConfig',
    items: [
      {
        title: '内部模型授权',
        path: '/app-config/llm-gateway-principals',
        icon: Settings2,
        requiredPermission: ADMIN_PERMISSIONS.gatewayPrincipalsView,
      },
      {
        title: 'LLM 路由管理',
        path: '/app-config/llm-routes',
        icon: Route,
        requiredPermission: ADMIN_PERMISSIONS.llmRoutesView,
      },
      {
        title: 'LLM 价格表',
        path: '/app-config/llm-pricing',
        icon: Coins,
        requiredPermission: ADMIN_PERMISSIONS.pricingView,
      },
      {
        title: 'LLM 访问策略',
        path: '/app-config/llm-access-policy',
        icon: ShieldCheck,
        requiredPermission: ADMIN_PERMISSIONS.llmAccessView,
      },
    ],
  });

  return (
    <>
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          type="button"
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          className="h-9 w-9 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-gray-100 transition-colors bg-white shadow-sm border border-border"
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <button
          type="button"
          className="fixed inset-0 bg-black/40 z-40 lg:hidden backdrop-blur-sm"
          aria-label="关闭侧边栏"
          onClick={() => setIsMobileOpen(false)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              setIsMobileOpen(false);
            }
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed left-0 top-0 z-50 h-screen bg-white border-r border-border transition-all duration-200 lg:translate-x-0',
          isCollapsed ? 'w-16' : 'w-48',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo Header with collapse button */}
          <div
            className={cn(
              'flex h-14 items-center border-b border-border',
              isCollapsed ? 'justify-center px-2' : 'px-3',
            )}
          >
            {/* Collapse toggle button - hamburger menu style */}
            <button
              type="button"
              onClick={toggleCollapsed}
              className="h-8 w-8 rounded-md flex-shrink-0 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-gray-100 transition-colors border border-border"
              title={isCollapsed ? '展开菜单' : '收起菜单'}
            >
              <Menu className="h-4 w-4" />
            </button>

            {!isCollapsed && (
              <div className="flex items-center gap-2 ml-2 min-w-0">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-accent text-white flex-shrink-0">
                  <LayoutDashboard className="h-3.5 w-3.5" />
                </div>
                <div className="flex flex-col min-w-0">
                  <span className="text-xs font-bold tracking-tight text-text-strong truncate">
                    TUTTI ADMIN
                  </span>
                  <span className="text-[9px] font-medium text-muted-foreground uppercase tracking-wider">
                    Dashboard
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <nav
            className={cn(
              'flex-1 overflow-y-auto py-4',
              isCollapsed ? 'px-2' : 'px-3',
            )}
          >
            {menuGroups.map((group) => (
              <div key={group.key} className="mb-4">
                {/* Group Header - hidden when collapsed */}
                {!isCollapsed && (
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.key)}
                    className="flex w-full items-center justify-between px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {group.title}
                    <ChevronDown
                      className={cn(
                        'h-3.5 w-3.5 transition-transform duration-200',
                        expandedGroups[group.key] ? 'rotate-0' : '-rotate-90',
                      )}
                    />
                  </button>
                )}

                {/* Group Items */}
                {(isCollapsed || expandedGroups[group.key]) && (
                  <div className={cn('space-y-0.5', !isCollapsed && 'mt-1')}>
                    {group.items
                      .filter(
                        (item) =>
                          !item.requiredPermission ||
                          authStore.can(item.requiredPermission),
                      )
                      .map((item) => {
                        const Icon = item.icon;
                        return (
                          <NavLink
                            key={item.path}
                            to={item.path}
                            end={item.exact}
                            target={item.openInNewTab ? '_blank' : undefined}
                            rel={
                              item.openInNewTab
                                ? 'noopener noreferrer'
                                : undefined
                            }
                            aria-label={
                              item.openInNewTab
                                ? `${item.title}（新标签页打开）`
                                : undefined
                            }
                            onClick={() => setIsMobileOpen(false)}
                            title={isCollapsed ? item.title : undefined}
                            className={({ isActive }) =>
                              cn(
                                'flex items-center rounded-md text-sm font-medium transition-all',
                                isCollapsed
                                  ? 'justify-center p-2.5'
                                  : 'gap-2.5 px-2.5 py-2',
                                isActive
                                  ? 'bg-gray-100 text-foreground font-semibold'
                                  : 'text-muted-foreground hover:bg-gray-100 hover:text-foreground',
                              )
                            }
                          >
                            <Icon className="h-4 w-4 flex-shrink-0" />
                            {!isCollapsed && (
                              <span className="truncate">{item.title}</span>
                            )}
                          </NavLink>
                        );
                      })}
                  </div>
                )}
              </div>
            ))}
          </nav>

          {/* Footer */}
          {!isCollapsed && (
            <div className="border-t border-border p-4">
              <div className="text-xs text-muted-foreground text-center">
                v1.0.0
              </div>
            </div>
          )}
        </div>
      </aside>
    </>
  );
});
